import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import {
  MembersListDataDocument,
  RemoveAllUserItemSharesDocument,
  RemoveAllUserWorkflowSharesDocument,
  SetAllUserItemSharesDocument,
  SetAllUserWorkflowSharesDocument,
  ShareItemDocument,
  ShareWorkflowDocument,
  type MemberAccessList,
  type ObjectAccessLevel,
  UserAccessListDocument,
} from "#app/lib/data";
import { AnalyticsContext, MemberEvent, trackEvent } from "#app/lib/analytics";

// One `member.update_access` per landed write; no ids leave the client.
const trackAccess = (data: Record<string, unknown>) => () =>
  trackEvent({ context: AnalyticsContext.Member, event: MemberEvent.UpdateAccess, data });

// Apollo plumbing for the settings members table's Access popover —
// the per-user inverse of the share dialogs. Reads `userAccessList`
// (granted workflows + add candidates + granted items) and reuses the
// shareWorkflow / shareItem upserts for level changes, removes (toggle
// off), and adds (toggle on). Every mutation refetches the access list
// AND the members list so the row's "N Workflows  N Items" counts stay
// in sync.

export type { MemberAccessList };

// Refetched by document so every active observation (the popover's list +
// the paginated members table) refreshes regardless of its variables.
const REFETCH_QUERIES = [UserAccessListDocument, MembersListDataDocument];

export function useMemberAccess(userId: string | null) {
  const variables = useMemo(() => ({ userId: userId ?? "" }), [userId]);
  const { data, loading } = useQuery(UserAccessListDocument, {
    variables,
    skip: !userId,
    fetchPolicy: "cache-and-network",
  });

  const refetchQueries = REFETCH_QUERIES;
  const [shareWorkflowMutation] = useMutation(ShareWorkflowDocument, {
    refetchQueries,
  });
  const [shareItemMutation] = useMutation(ShareItemDocument, {
    refetchQueries,
  });
  const [setAllWorkflowsMutation] = useMutation(
    SetAllUserWorkflowSharesDocument,
    { refetchQueries },
  );
  const [setAllItemsMutation] = useMutation(SetAllUserItemSharesDocument, {
    refetchQueries,
  });
  const [removeAllWorkflowsMutation] = useMutation(
    RemoveAllUserWorkflowSharesDocument,
    { refetchQueries },
  );
  const [removeAllItemsMutation] = useMutation(
    RemoveAllUserItemSharesDocument,
    { refetchQueries },
  );

  // Workflow grants: add / level-change keep enabled=true; remove
  // flips enabled=false (row + level preserved so re-adding restores
  // the prior tier).
  const setWorkflowAccess = useCallback(
    (workflowId: string, accessLevel: ObjectAccessLevel, enabled: boolean) => {
      if (!userId) return;
      void shareWorkflowMutation({
        variables: {
          input: { workflowId, userId, accessLevel, enabled },
        },
      }).then(trackAccess({ object: "workflow", accessLevel, enabled, bulk: false }));
    },
    [shareWorkflowMutation, userId],
  );

  const setItemAccess = useCallback(
    (issueId: string, accessLevel: ObjectAccessLevel, enabled: boolean) => {
      if (!userId) return;
      void shareItemMutation({
        variables: {
          input: { issueId, userId, accessLevel, enabled },
        },
      }).then(trackAccess({ object: "issue", accessLevel, enabled, bulk: false }));
    },
    [shareItemMutation, userId],
  );

  // Bulk tier set over the user's DIRECT rows (a team-derived row is left
  // to its team) — one server mutation, one refetch.
  const setAllWorkflows = useCallback(
    (accessLevel: ObjectAccessLevel) => {
      if (!userId) return;
      void setAllWorkflowsMutation({ variables: { userId, accessLevel } }).then(
        trackAccess({ object: "workflow", accessLevel, enabled: true, bulk: true }),
      );
    },
    [setAllWorkflowsMutation, userId],
  );

  const setAllItems = useCallback(
    (accessLevel: ObjectAccessLevel) => {
      if (!userId) return;
      void setAllItemsMutation({ variables: { userId, accessLevel } }).then(
        trackAccess({ object: "issue", accessLevel, enabled: true, bulk: true }),
      );
    },
    [setAllItemsMutation, userId],
  );

  const removeAllWorkflows = useCallback(() => {
    if (!userId) return;
    void removeAllWorkflowsMutation({ variables: { userId } }).then(
      trackAccess({ object: "workflow", enabled: false, bulk: true }),
    );
  }, [removeAllWorkflowsMutation, userId]);

  const removeAllItems = useCallback(() => {
    if (!userId) return;
    void removeAllItemsMutation({ variables: { userId } }).then(
      trackAccess({ object: "issue", enabled: false, bulk: true }),
    );
  }, [removeAllItemsMutation, userId]);

  return {
    loading,
    accessList: (data?.userAccessList ?? null) as MemberAccessList | null,
    setWorkflowAccess,
    setItemAccess,
    setAllWorkflows,
    setAllItems,
    removeAllWorkflows,
    removeAllItems,
  };
}
